import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as ContentCopyIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuthStore } from '@store/authStore';

interface AdminManagementProps {
  academyId: string;
}

const emptyForm = {
  firstName: '',
  lastName: '',
  contactEmail: '',
  contactPhone: '',
  isSuperAdmin: false,
};

const AdminManagement: React.FC<AdminManagementProps> = ({ academyId }) => {
  const { user } = useAuthStore();
  const [admins, setAdmins] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<any>(null);
  const [formData, setFormData] = useState(emptyForm);
  const [issuedCredentials, setIssuedCredentials] = useState<{ adminIdCode: string; temporaryPassword?: string | null } | null>(null);

  const isEditingCurrentSuperAdmin = editingAdmin &&
    user?.id &&
    (editingAdmin.id === user.id || editingAdmin.userId === user.id) &&
    (editingAdmin.isSuperAdmin === true || editingAdmin.is_super_admin === true);

  useEffect(() => {
    if (academyId) {
      loadAdmins();
    } else {
      console.error('[AdminManagement] Academy ID is undefined');
    }
  }, [academyId]);

  const loadAdmins = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/academies/${academyId}/admins`);
      const result = await response.json();

      if (result.success) {
        const adminsData = (result.data || []).map((admin: any) => ({
          ...admin,
          isSuperAdmin: admin.isSuperAdmin === true || admin.is_super_admin === true
        }));
        setAdmins(adminsData);
      } else {
        setError(result.message || 'Failed to load admins');
      }
    } catch (err: any) {
      console.error('Error loading admins:', err);
      setError('Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!formData.firstName || !formData.lastName) {
      toast.error('First name and last name are required');
      return;
    }

    setLoading(true);
    try {
      const url = editingAdmin
        ? `/api/academies/${academyId}/admins/${editingAdmin.id}`
        : `/api/academies/${academyId}/admins`;

      const requestBody = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        contactEmail: formData.contactEmail || undefined,
        contactPhone: formData.contactPhone || undefined,
        isSuperAdmin: formData.isSuperAdmin,
      };

      const response = await fetch(url, {
        method: editingAdmin ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const result = await response.json();

      if (result.success) {
        if (!editingAdmin) {
          setIssuedCredentials({
            adminIdCode: result.data?.adminIdCode,
            temporaryPassword: result.data?.temporaryPassword,
          });
          toast.success('Admin ID created. Share it offline or by email.');
        } else {
          toast.success('Admin updated successfully');
          setDialogOpen(false);
          setEditingAdmin(null);
          setFormData(emptyForm);
        }
        loadAdmins();
      } else {
        toast.error(result.message || `Failed to ${editingAdmin ? 'update' : 'add'} admin`);
      }
    } catch (err: any) {
      console.error('[AdminManagement] Error saving admin:', err);
      toast.error(`Failed to ${editingAdmin ? 'update' : 'add'} admin: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = (admin: any) => {
    setEditingAdmin(admin);
    setIssuedCredentials(null);
    const isSuperAdmin = admin.isSuperAdmin === true || admin.is_super_admin === true;
    setFormData({
      firstName: admin.firstName || admin.first_name || '',
      lastName: admin.lastName || admin.last_name || '',
      contactEmail: admin.contactEmail || admin.email || '',
      contactPhone: admin.phone || admin.contactPhone || '',
      isSuperAdmin,
    });
    setDialogOpen(true);
  };

  const handleDeleteAdmin = async (adminId: string) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/academies/${academyId}/admins/${adminId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        toast.success('Admin deleted successfully');
        loadAdmins();
      } else {
        toast.error(result.message || 'Failed to delete admin');
      }
    } catch (err: any) {
      console.error('Error deleting admin:', err);
      toast.error('Failed to delete admin');
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.info(value);
    }
  };

  if (loading && admins.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">Admin Management</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Admins sign in with Admin ID. Contact email and mobile are for communications only.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingAdmin(null);
            setIssuedCredentials(null);
            setFormData(emptyForm);
            setDialogOpen(true);
          }}
        >
          Generate Admin ID
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Admin ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Contact (not login)</TableCell>
              <TableCell>Super Admin</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No admins found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin.id}>
                  <TableCell>
                    <Typography sx={{ fontFamily: 'ui-monospace, Consolas, monospace', fontWeight: 700 }}>
                      {admin.adminIdCode || 'Email login (legacy)'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {`${admin.firstName || ''} ${admin.lastName || ''}`.trim() || '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{admin.contactEmail || '—'}</Typography>
                    <Typography variant="body2" color="text.secondary">{admin.phone || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {(admin.isSuperAdmin === true || admin.is_super_admin === true) ? (
                      <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 'bold' }}>
                        Yes
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditAdmin(admin)}
                      title="Edit Admin"
                    >
                      <EditIcon />
                    </IconButton>
                    {!(admin.isSuperAdmin === true || admin.is_super_admin === true) && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteAdmin(admin.id)}
                        title="Delete Admin"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingAdmin(null);
          setIssuedCredentials(null);
          setFormData(emptyForm);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{editingAdmin ? 'Edit academy admin' : 'Generate Admin ID'}</DialogTitle>
        <DialogContent>
          {issuedCredentials ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="success">
                Share this Admin ID and password offline or by email. The contact details are not a login.
              </Alert>
              <TextField
                label="Admin ID"
                value={issuedCredentials.adminIdCode}
                InputProps={{ readOnly: true }}
              />
              {issuedCredentials.temporaryPassword ? (
                <TextField
                  label="Temporary password (shown once)"
                  value={issuedCredentials.temporaryPassword}
                  InputProps={{ readOnly: true }}
                />
              ) : null}
              <Button
                startIcon={<ContentCopyIcon />}
                onClick={() => copyText(
                  issuedCredentials.temporaryPassword
                    ? `${issuedCredentials.adminIdCode} / ${issuedCredentials.temporaryPassword}`
                    : issuedCredentials.adminIdCode,
                  'Credentials',
                )}
                sx={{ textTransform: 'none' }}
              >
                Copy credentials
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Alert severity="info">
                {editingAdmin
                  ? 'Contact email and mobile are for communications only.'
                  : 'Creates an Admin ID the person can sign in with. EncycGames can also issue extra IDs for this academy.'}
              </Alert>
              <TextField
                fullWidth
                label="First Name *"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              />
              <TextField
                fullWidth
                label="Last Name *"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              />
              <TextField
                fullWidth
                label="Contact email (not login)"
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
              />
              <TextField
                fullWidth
                label="Contact mobile (not login)"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isSuperAdmin}
                    onChange={(e) => setFormData({ ...formData, isSuperAdmin: e.target.checked })}
                    color="primary"
                    disabled={isEditingCurrentSuperAdmin}
                  />
                }
                label="Super Admin"
              />
              {isEditingCurrentSuperAdmin && (
                <Alert severity="warning">
                  You cannot depromote yourself from Super Admin role. The user who registered during academy creation must remain a Super Admin.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setEditingAdmin(null); setIssuedCredentials(null); setFormData(emptyForm); }}>
            {issuedCredentials ? 'Done' : 'Cancel'}
          </Button>
          {!issuedCredentials && (
            <Button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleAddAdmin();
              }}
              variant="contained"
              disabled={loading}
            >
              {loading ? (editingAdmin ? 'Updating...' : 'Generating...') : (editingAdmin ? 'Update Admin' : 'Generate Admin ID')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminManagement;
